<script setup lang="ts">
import { type CalendarDate, DateFormatter, type DateValue, getLocalTimeZone, today } from '@internationalized/date'
import { CalendarIcon, X } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import type { HTMLAttributes } from 'vue'

import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn, fromCalendarDate, toCalendarDate } from '@/lib/utils'

// PopoverRoot renders its slot directly with no wrapping element, so this component's rendered
// tree has two roots (the trigger button + the teleported content) — Vue disables automatic
// class/attrs inheritance for multi-root components. `class` must be forwarded explicitly onto
// the trigger button below, or every caller's width override (e.g. `class="w-36"`) is silently dropped.
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    modelValue: string
    id?: string
    placeholder?: string
    min?: string // YYYY-MM-DD
    max?: string // YYYY-MM-DD
    disabled?: boolean
    required?: boolean
    invalid?: boolean
    class?: HTMLAttributes['class']
  }>(),
  {
    placeholder: 'Select date',
    disabled: false,
    required: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [string]
}>()

const isOpen = ref(false)

const df = new DateFormatter('en-AU', { dateStyle: 'medium' })

const calendarValue = computed(() => toCalendarDate(props.modelValue))
const minValue = computed(() => toCalendarDate(props.min))
const maxValue = computed(() => toCalendarDate(props.max))

// Drives which month the calendar displays.
// Recomputed every time the popover opens so it always reflects the current value
// or falls back to today — even after external resets (e.g. "Clear filters").
const calendarPlaceholder = ref<DateValue>(
  (calendarValue.value || maxValue.value || minValue.value || today(getLocalTimeZone())) as DateValue,
)

function syncPlaceholder() {
  calendarPlaceholder.value = calendarValue.value || maxValue.value || minValue.value || today(getLocalTimeZone())
}

watch(isOpen, (open) => {
  if (open) syncPlaceholder()
})

const displayValue = computed(() =>
  calendarValue.value ? df.format(calendarValue.value.toDate(getLocalTimeZone())) : null,
)

function onSelect(val: DateValue | undefined) {
  emit('update:modelValue', val ? fromCalendarDate(val as CalendarDate) : '')
  isOpen.value = false
}

function clear(e: MouseEvent) {
  e.stopPropagation()
  emit('update:modelValue', '')
}
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <button
        :id="id"
        type="button"
        :disabled="disabled"
        :class="
          cn(
            invalid ? 'border-destructive focus-visible:ring-destructive' : 'border-input focus-visible:ring-ring',
            'bg-background ring-offset-background',
            'flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm',
            'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            !displayValue && 'text-muted-foreground',
            props.class,
          )
        "
      >
        <span class="flex items-center gap-2">
          <CalendarIcon class="size-4 shrink-0 opacity-50" />
          <span>{{ displayValue ?? placeholder }}</span>
        </span>
        <button
          v-if="modelValue && !disabled"
          type="button"
          tabindex="-1"
          aria-label="Clear date"
          class="text-muted-foreground hover:text-foreground ml-1 rounded p-0.5 transition-colors"
          @click="clear"
        >
          <X class="size-3.5" />
        </button>
      </button>
    </PopoverTrigger>

    <PopoverContent class="w-auto p-0" align="start">
      <Calendar
        v-model="calendarValue"
        class="calendar-no-double-chevron"
        :default-placeholder="(calendarPlaceholder as DateValue)"
        :min-value="minValue"
        :max-value="maxValue"
        locale="en-AU"
        layout="month-and-year"
        initial-focus
        @update:model-value="onSelect"
      />
    </PopoverContent>
  </Popover>
</template>

<style>
/*
 @tailwindcss/forms adds a background-image chevron on <select> elements.
 The NativeSelect component already renders its own ChevronDownIcon,
 so suppress the plugin arrow to avoid a double-chevron.
 Must be unscoped because PopoverContent teleports outside this component's DOM.
*/
.calendar-no-double-chevron select {
  background-image: none;
}
</style>
